/* eslint-disable @typescript-eslint/no-explicit-any */
import { makeAutoObservable, reaction } from 'mobx';
import { ReactElement } from 'react';

import { Module } from '@types';
import {
  getPercentComplete,
  sendEventToHost,
  addToRollbarContext,
} from '@utils';

const DEFAULT_SHOW_BACK_BTN = false;

export default class FormStore {
  formConfig: { modules: Module[] } = { modules: [] };
  currentIndex = 0;
  currentModuleName: string = 'DEFAULT';
  currentModuleTitle: string = 'Default';
  currentModule?: Module | undefined;
  isRampingOff = false;
  hasApplicationStarted = false;
  moduleElements: ReactElement[] = [];
  formData: any = {
    firstName: '',
    lastName: '',
    zipCode: '',
    state: '',
    debtAmount: '',
    debtGoal: '',
    email: '',
    phone: '',
    debtType: '',
    paymentStatus: '',
    debtCause: '',
    employmentStatus: '',
    householdIncome: '',
    incomeConsistency: '',
    creditScore: '',
    creditScoreImportance: '',
    propertyType: '',
    futureLoanPlans: {
      none: false,
      auto_in_2_years: false,
      mortgage_in_2_years: false,
    },
    leadType: '',
  };

  constructor() {
    makeAutoObservable(this);

    reaction(
      () => this.currentModuleName,
      (currentModuleName) => {
        addToRollbarContext({ currentModule: currentModuleName });
      }
    );
    reaction(
      () => this.currentIndex,
      (currentIndex) => {
        const modules = this.formConfig.modules;
        this.currentModule = modules[currentIndex];
        this.currentModuleName = modules[currentIndex].name;
        this.currentModuleTitle = modules[this.currentIndex].title;
      }
    );
  }

  get getSubmodule() {
    return this.currentModule?.subModules;
  }

  get getModuleElements() {
    return this.moduleElements[this.currentIndex];
  }

  get getShowBackBtn() {
    return this.currentModule?.showBackBtn || DEFAULT_SHOW_BACK_BTN;
  }

  setFormConfig(formConfig: { modules: Module[] }) {
    this.formConfig = formConfig;
    const { modules } = formConfig;
    if (modules.length > 0) {
      this.currentModule = modules[0];
      this.currentModuleName = modules[0].name;
    }
  }

  setToggleRampingOff() {
    this.isRampingOff = !this.isRampingOff;
  }

  setApplicationStarted() {
    this.hasApplicationStarted = true;
  }

  setModuleElements(modules: ReactElement[]) {
    this.moduleElements = modules;
  }

  setFormData(data: object) {
    this.formData = data;
  }

  updateFormData(data: object) {
    this.formData = { ...this.formData, ...data };
  }

  next() {
    const modules = this.formConfig.modules;
    if (this.currentIndex < modules.length - 1) {
      this.currentIndex++;
    }
    const percentComplete = getPercentComplete(
      this.formConfig,
      this.currentModuleName
    );
    let data = {
      moduleTitle: this.currentModuleTitle,
    } as { [key: string]: string | number };
    if (percentComplete) {
      data = { ...data, percentComplete };
    }
    sendEventToHost('progress_to_host', {
      data,
    });
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
    }
  }

  exit(data: object) {
    sendEventToHost('close', { data });
  }

  isLastModule() {
    return this.currentIndex === this.formConfig.modules.length - 1;
  }

  clearFormStore() {
    this.formConfig = { modules: [] };
    this.currentIndex = 0;
    this.isRampingOff = false;
    this.hasApplicationStarted = false;
    this.moduleElements = [];
  }

  previousModule(name: string): string {
    const currentIndex = this.formConfig.modules.findIndex(
      (m) => m.name === name
    );
    if (currentIndex > 0) {
      return this.formConfig.modules[currentIndex - 1].name;
    } else {
      return '';
    }
  }
}
